import renderDateField from 'components/DateField/reduxFormDateField';
import renderField from 'components/Field/reduxForm';
import renderSelect from 'components/Select/reduxForm';
import { isObject } from 'lodash';
import moment from 'utils/moment';
import * as inputFilters from 'utils/reduxForm/inputFilters';

import {
  getRequiredFn,
  getValidateMaxDateFn,
  isValidDate,
  validateCyrillicMiddleName,
  validateCyrillicName,
  validatePhone,
  validateSnils,
} from 'utils/reduxForm/validations';
import * as parsers from '../../../../utils/reduxForm/parsers';

const required = getRequiredFn();
const availableDate = moment().endOf('day');

export default Object.freeze({
  lastName: {
    name: 'lastName',
    placeholder: 'Фамилия',
    label: 'Фамилия',
    component: renderField,
    maxLength: 60,
    isRequired: true,
    validate: [required, validateCyrillicName],
    getter: (props) => props.personalInfo.get('data').lastName,
    normalize: inputFilters.cyrillicName,
  },
  firstName: {
    name: 'firstName',
    placeholder: 'Имя',
    label: 'Имя',
    component: renderField,
    maxLength: 60,
    isRequired: true,
    validate: [required, validateCyrillicName],
    getter: (props) => props.personalInfo.get('data').firstName,
    normalize: inputFilters.cyrillicName,
  },
  middleName: {
    name: 'middleName',
    placeholder: 'Отчество',
    label: 'Отчество',
    component: renderField,
    maxLength: 60,
    validate: validateCyrillicMiddleName,
    getter: (props) => props.personalInfo.get('data').middleName,
    normalize: inputFilters.cyrillicMiddleName,
  },
  dateOfBirth: {
    name: 'dateOfBirth',
    placeholder: 'Дата рождения',
    label: 'Дата рождения',
    component: renderDateField,
    maxDate: availableDate,
    getter: (props) => props.personalInfo.get('data').dateOfBirth,
    parse: parsers.momentParser,
    validate: [isValidDate, getValidateMaxDateFn(availableDate)],
  },
  registrationDate: {
    name: 'registrationDate',
    placeholder: 'Дата регистрации по адресу места жительства (места пребывания)',
    label: 'Дата регистрации по адресу места жительства (места пребывания)',
    component: renderDateField,
    maxDate: availableDate,
    getter: (props) => props.personalInfo.get('data').residenceRegistrationDate,
    isRequired: true,
    parse: parsers.momentParser,
    disabled: (props) =>
      props.personalInfo.get('data') && props.personalInfo.get('data').residenceRegistrationDate,
    validate: [required, isValidDate, getValidateMaxDateFn(availableDate)],
  },
  placeOfBirth: {
    name: 'placeOfBirth',
    placeholder: 'Место рождения',
    label: 'Место рождения',
    maxLength: 128,
    component: renderField,
    getter: (props) => props.personalInfo.get('data').placeOfBirth,
  },
  inn: {
    name: 'inn',
    placeholder: 'ИНН',
    label: 'ИНН',
    component: renderField,
    disabled: true,
    getter: (props) => props.user.getIn(['data', 'inn']),
  },
  phone: {
    name: 'phone',
    placeholder: 'Телефон',
    label: 'Телефон',
    component: renderField,
    isRequired: true,
    mask: '+7(111)111-11-11',
    validate: validatePhone,
    getter: (props) => props.contactInfo.get('data').phone,
  },
  email: {
    name: 'email',
    placeholder: 'Адрес электронной почты',
    label: 'Адрес электронной почты',
    component: renderField,
    isRequired: true,
    validate: required,
    getter: (props) => props.contactInfo.get('data').email,
  },
  address: {
    name: 'address',
    placeholder: '',
    label:
      'Адрес места жительства* (места пребывания – при отсутствии места жительства на территории РФ)',
    component: renderField,
    validate: required,
    disabled: true,
    getter: (props) => props.personalInfo.get('data').residenceAddress,
  },
  documentTypes: {
    name: 'documentType',
    placeholder: 'Вид документа',
    label: 'Вид документа',
    component: renderSelect,
    isFull: true,
    isRequired: true,
    validate: required,
    parse: (val) => val.value,
    getter: (props) => props.personalInfo.get('data').code,
    options: (props) =>
      props.documentTypes.get('data').map((doc) => ({
        label: doc.name,
        value: doc.code,
      })),
    saveChangesTo: 'selectedCode',
  },
  documentNumber: {
    name: 'documentNumber',
    placeholder: 'Серия и номер документа',
    label: 'Серия и номер документа',
    maxLength: 25,
    isRequired: true,
    validate: required,
    component: renderField,
    getter: (props) => props.personalInfo.get('data').dulNumber,
  },
  documentDate: {
    name: 'documentDate',
    placeholder: 'Дата выдачи документа',
    label: 'Дата выдачи документа',
    component: renderDateField,
    getter: (props) => props.personalInfo.get('data').dulDate,
    maxDate: availableDate,
    isRequired: true,
    validate: [required, isValidDate, getValidateMaxDateFn(availableDate)],
    parse: parsers.momentParser,
  },
  issuedBy: {
    name: 'issuedBy',
    placeholder: 'Кем выдан',
    label: 'Кем выдан',
    maxLength: 255,
    component: renderField,
    isRequired: true,
    validate: required,
    getter: (props) => props.personalInfo.get('data').dulIssuedBy,
  },
  departmentCode: {
    name: 'departmentCode',
    placeholder: 'Код подразделения',
    label: 'Код подразделения',
    maxLength: 20,
    component: renderField,
    isVisible: {
      check: (selectedCode) => {
        let code = isObject(selectedCode) ? selectedCode.value : selectedCode;
        return code === '21'; //passport code
      },
      stateField: 'selectedCode',
    },
    getter: (props) => props.personalInfo.get('data').dulUnit,
  },
  snils: {
    name: 'snils',
    placeholder: 'СНИЛС',
    label: 'СНИЛС',
    component: renderField,
    mask: '111-111-111 11',
    autoComplete: 'off',
    validate: validateSnils,
    getter: (props) => props.personalInfo.get('data').snils,
  },
  gender: {
    name: 'gender',
    placeholder: 'Пол',
    label: 'Пол',
    isFull: true,
    isRequired: true,
    validate: required,
    component: renderSelect,
    parse: (val) => val.value,
    getter: (props) => props.personalInfo.get('data').gender,
    options: [
      { value: 1, label: 'Мужской' },
      { value: 2, label: 'Женский' },
    ],
  },
});
